<template>
  <!-- 재고리스트 — 해외창고 관리(warehouseManagement) 「库存现况」 탭과 같은 API(getstockmanagement slim)·CDataTable 을 쓴다.
       그 탭은 상태 10종을 한꺼번에 보는 현황이고, 여기는 창고에 실제로 놓인 재고(위치번호)를 찾는 목록이다.
       재고 관련 탭(库存现况·收发簿·手动入库·海外仓库库存修改)도 여기로 모았다(2026-10-03) — warehouseManagement 를 section="stock" 으로
       그대로 끼운다(기능·API 동일). 「出库」 메뉴에는 출고 탭만 남는다.
       2026-10 YZY: 오른쪽 큰 탭(库位库存/库存管理)을 없애고 아래 한 줄 탭으로 합쳤다 —
       [中国库存 | 待上架库存 | 库存现况 | 出入库记录 | 手动入库 | 库存修改]. 뒤 넷은 warehouseManagement 의 탭을 tab prop 으로 고른다 -->
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div class="card-body pb-0">
            <div class="btn-group btn-group-sm flex-wrap">
              <button v-for="t in allTabs" :key="t.key" type="button"
                      :class="['btn', currentTab === t.key ? 'btn-primary' : 'btn-outline-primary']"
                      @click="selectTab(t.key)">{{ t.text }}</button>
            </div>
          </div>
          <div v-if="view === 'list'" class="card-body">
            <div class="d-flex flex-wrap align-items-center mb-3" style="gap:8px">
              <label class="mb-0 small ml-2">货主(업체)</label>
              <input type="text" v-model.trim="companyid" list="stockListCompanyList" placeholder="全部(전체)"
                     class="form-control form-control-sm" style="width:160px" @keyup.enter="search">
              <datalist id="stockListCompanyList">
                <option v-for="c in companyOptions" :key="c" :value="c"></option>
              </datalist>
              <b-button size="sm" variant="primary" @click="search" :disabled="loading">搜索<small>(검색)</small></b-button>
              <label class="mb-0 small ml-2">库位号/商品/条码(위치·상품·바코드)</label>
              <input type="text" v-model.trim="keyword" class="form-control form-control-sm" style="width:180px" placeholder="A-01-01">
              <b-button size="sm" variant="success" @click="downloadExcel" :disabled="!filteredRows.length">⬇ Excel<small>(엑셀 다운로드)</small></b-button>
              <span class="small text-muted">共 {{ filteredRows.length }}件 · 数量合计(수량합계) {{ totalQty.toLocaleString() }}</span>
            </div>

            <!-- 待上架库存: 위치번호 라벨 인쇄 + 스캔 재고확정(입고리스트 재고대기 탭과 같은 컴포넌트) -->
            <location-label-scan v-if="status === '재고대기'" @confirmed="search"></location-label-scan>

            <div v-if="loading" class="text-center my-4"><b-spinner variant="primary"></b-spinner></div>
            <CDataTable v-else :headers="headers" :items="filteredRows" item-key="_id" storage-key="logisStockListV0">
              <template v-slot:_이미지="{ item }">
                <a v-if="item._이미지" :href="item._이미지" target="_blank" rel="noopener">
                  <img :src="item._이미지" alt="" style="width:48px;height:48px;object-fit:cover">
                </a>
              </template>
              <template v-slot:제품위치="{ item }">
                <b v-if="item.제품위치">{{ item.제품위치 }}</b>
                <span v-else class="text-muted">-</span>
              </template>
            </CDataTable>
          </div>
          <!-- 한 번 띄운 뒤에는 숨기기만 한다 — 탭을 오갈 때마다 전체 재고(수만 건)를 다시 받지 않게 -->
          <div v-if="manageLoaded" v-show="view === 'manage'" class="card-body p-0">
            <warehouse-management section="stock" :tab="manageTab"></warehouse-management>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import CDataTable from '@/components/common/CDataTable';
import LocationLabelScan from './LocationLabelScan';
import WarehouseManagement from './warehouseManagement';
import { coupassMenuTitle } from './coupassMenu';

// 货主库存出库(shipout) 탭은 사이드바 독립 메뉴(logis.logisshipoutrequest)로 나갔다(2026-10 YZY) — 옛 ?view=shipout 주소는 그리로 보낸다
// 库存管理(warehouseManagement section="stock") 안의 탭 — 순서 = 그 컴포넌트의 보이는 탭 순서(tab prop 번호)
const MANAGE_TABS = [
  { key: 'inventory', text: '库存现况(재고현황)' },
  { key: 'history', text: '出入库记录(입출고이력)' },
  { key: 'manual', text: '手动入库(수동입고)' },
  { key: 'audit', text: '库存修改(재고수정)' }
];

export default {
  name: 'LogisStockListV0',
  components: { CDataTable, LocationLabelScan, WarehouseManagement },
  data() {
    return {
      view: 'list',
      manageTab: 0,
      manageLoaded: false,
      pageTitle: coupassMenuTitle('logis.logisstocklist'),
      tabs: [
        { value: '중국재고', text: '中国库存(중국재고)' },
        { value: '재고대기', text: '待上架库存(재고대기)' }
      ],
      status: '중국재고',
      companyid: '',
      keyword: '',
      companyOptions: [],
      rows: [],
      loading: false,
      // 열 순서 = 엑셀 열 순서(downloadExcel 이 headers 를 그대로 쓴다). 창고가 찾는 순서로(2026-10 YZY):
      // 库位号 → 货主 → 图片 → 条码 → 数量 → SKUID → 选项 → 商品名, 날짜 두 칸은 맨 뒤
      headers: [
        { text: '库位号(위치번호)', value: '제품위치', isSlot: true },
        { text: '货主(업체)', value: 'companyid' },
        { text: '图片(이미지)', value: '_이미지', isSlot: true },
        { text: '条码(바코드)', value: '바코드' },
        { text: '数量(수량)', value: '_수량', align: 'right' },
        { text: 'SKUID', value: 'SKUID' },
        { text: '选项(옵션)', value: '_옵션' },
        { text: '商品名(상품명)', value: '상품명' },
        { text: '入库日(입고일)', value: '_입고일' },
        { text: '上架日(재고확정일)', value: '_확정일' }
      ]
    };
  },
  computed: {
    // 한 줄 탭: 위치별 재고 두 칸 + 재고관리 네 칸
    allTabs() {
      return this.tabs.map(t => ({ key: t.value, text: t.text })).concat(MANAGE_TABS);
    },
    currentTab() {
      return this.view === 'list' ? this.status : MANAGE_TABS[this.manageTab].key;
    },
    filteredRows() {
      const q = this.keyword.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter(r => [r.제품위치, r.상품명, r.바코드, r.SKUID].some(v => v && String(v).trim().toLowerCase().includes(q)));
    },
    totalQty() {
      return this.filteredRows.reduce((s, r) => s + (Number(r._수량) || 0), 0);
    }
  },
  watch: {
    '$route.query.view'(v) {
      if (this.$route.name !== 'logis.logisstocklist') return;   // 상단 탭으로 숨은 동안 다른 메뉴 주소에 반응하지 않게
      this.applyQuery(v);
    }
  },
  mounted() {
    if (this.$route.query.view === 'shipout') {
      this.$router.replace({ name: 'logis.logisshipoutrequest' }).catch(e => console.error('[库存列表] 출고요청 메뉴로 이동 실패', e));
      return;
    }
    this.applyQuery(this.$route.query.view);
    if (this.view === 'list') this.search();
  },
  methods: {
    // 주소의 ?view= — 옛 주소 view=manage 는 库存现况 으로 연다
    applyQuery(v) {
      const i = MANAGE_TABS.findIndex(t => t.key === v);
      if (v === 'manage' || i >= 0) {
        this.view = 'manage';
        this.manageTab = Math.max(i, 0);
        this.manageLoaded = true;
      } else {
        this.view = 'list';
      }
    },
    selectTab(key) {
      if (this.currentTab === key) return;
      const i = MANAGE_TABS.findIndex(t => t.key === key);
      const query = { ...this.$route.query };
      if (i >= 0) {
        this.view = 'manage';
        this.manageTab = i;
        this.manageLoaded = true;
        query.view = key;
      } else {
        this.view = 'list';
        this.status = key;
        delete query.view;
        this.search();
      }
      if (query.view !== this.$route.query.view) {
        this.$router.replace({ query }).catch(e => { if (e && e.name !== 'NavigationDuplicated') console.error('[库存列表] 탭 주소 갱신 실패', e); });
      }
    },
    ymd(v) {
      if (!v) return '';
      const d = new Date(typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v) : v);
      if (isNaN(d.getTime())) return String(v).slice(0, 10);
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },
    async search() {
      this.loading = true;
      try {
        // 해외창고 관리 화면과 같은 호출. slim 이라 화면이 쓰는 필드만 온다(재고리스트용 위치·입고일 포함)
        const res = await axios.post('/api/reqpurchase/getstockmanagement', {
          companyid: this.companyid || '', status: [this.status], slim: true
        });
        if (res.data.요청결과 !== 'ok') {
          alert('搜索失败(검색 실패)');
          this.rows = [];
          return;
        }
        this.rows = (res.data.reqpurchase || []).map(r => ({
          ...r,
          // 바코드 앞뒤 공백 — 저장값에 섞여 있는 건이 있다. 표시·검색·스캔·엑셀은 공백을 뺀 값으로(저장값은 그대로)
          바코드: typeof r.바코드 === 'string' ? r.바코드.trim() : r.바코드,
          _이미지: r.옵션이미지 || r.등록이미지 || '',
          _옵션: [r.옵션1_중국어, r.옵션2_중국어].filter(Boolean).join(' '),
          _수량: parseInt(r.입고수량) || parseInt(r.구매요청수량) || 0,
          _입고일: this.ymd(r.입고날짜),
          _확정일: this.ymd(r.재고확정일시)
        })).sort((a, b) => String(a.제품위치 || '~').localeCompare(String(b.제품위치 || '~')) || (Number(b.입고날짜) || 0) - (Number(a.입고날짜) || 0));
        const set = new Set(this.companyOptions);
        this.rows.forEach(r => { if (r.companyid) set.add(r.companyid); });
        this.companyOptions = [...set].sort();
      } catch (e) {
        console.error('재고리스트 검색 오류:', e);
        alert('搜索错误(검색 오류): ' + e.message);
      } finally {
        this.loading = false;
      }
    },
    downloadExcel() {
      const cols = this.headers;
      const IMG = cols.findIndex(h => h.value === '_이미지');
      // 이미지 칸은 화면 썸네일 대신 「link」 글자 + 하이퍼링크(입고리스트와 같은 방식)
      const cell = (r, h) => {
        if (h.value === '_이미지') return r._이미지 ? 'link' : '';
        const v = r[h.value];
        return v == null ? '' : (typeof v === 'string' ? v.trim() : v);
      };
      const aoa = [cols.map(h => h.text)].concat(this.filteredRows.map(r => cols.map(h => cell(r, h))));
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      this.filteredRows.forEach((r, i) => {
        const c = ws[XLSX.utils.encode_cell({ r: i + 1, c: IMG })];
        if (r._이미지 && c) c.l = { Target: r._이미지, Tooltip: r._이미지 };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const d = new Date();
      const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      XLSX.writeFile(wb, '库存列表_' + this.status + '_' + stamp + '.xlsx');
    }
  }
};
</script>
